import React from 'react';
import { UseMutateFunction } from '@tanstack/react-query';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import cn from 'classnames';
import { utcToZonedTime } from 'date-fns-tz';

import { AdaptiveTableCell, IconCell } from '@components/common';

import { formatDate } from '@utils/date';

import { TSelectOption } from '@interfaces/components/componentTypes';
import { IAssetRentRequest, IAssetRentRequestCardItem } from '@interfaces/rent/assetRent';

import { ERequestItemChanges, ERequestItemChangesAcceptance } from '@enums/asset';

import { timezoneUTC } from '@constants/common';
import { columnSizes } from '@constants/table';

import { ActionCellForAssetRentRequestTable } from './ActionCellForAssetRentRequestTable';

import styles from './RequestTable.module.scss';

interface IGetColumnsParams {
	handleViewOnMap: (assetId: string) => Promise<void>;
	openProposeDatesModal: (selectedItem: TSelectOption) => void;
	acceptProposedAsset: UseMutateFunction<any, any, { assetId: string }, any>;
	declineProposedAsset: UseMutateFunction<any, any, { assetId: string }, any>;
	isAO: boolean;
	handleOpenApprovalInfo: (asset: IAssetRentRequestCardItem) => void;
	request: IAssetRentRequest;
	removeProposedAsset: (id: string) => void;
	openProfileHandler: (assetDbId: string) => void;
	isAssetItemsLoading?: boolean;
}

export const getColumns = ({
	handleViewOnMap,
	openProposeDatesModal,
	openProfileHandler,
	acceptProposedAsset,
	declineProposedAsset,
	isAO,
	handleOpenApprovalInfo,
	request,
	removeProposedAsset,
	isAssetItemsLoading,
}: IGetColumnsParams): Array<ColumnDef<any>> => ([
	{
		header: 'Asset ID',
		accessorKey: 'assetId',
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value} />;
		},
	},
	{
		header: 'Asset Type',
		accessorKey: 'type',
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return (
				<IconCell
					id={value?.icon?.id}
					text={value?.assetTypeName}
				/>
			);
		},
	},
	{
		header: 'Asset Subtype',
		accessorKey: 'subType',
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value?.assetSubTypeName || ''} />;
		},
	},
	{
		header: 'Price',
		accessorKey: 'price',
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value ? `£ ${value}` : ''} />;
		},
	},
	{
		header: 'Asset Address',
		accessorKey: 'assetAddress',
		enableSorting: false,
		size: columnSizes.size450,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value || ''} />;
		},
	},
	{
		header: 'START RENT DATE',
		accessorKey: 'startRentDate',
		cell: ({ getValue, row }: CellContext<IAssetRentRequestCardItem, any>) => {
			const startRentDate = getValue();
			const { proposalInfo } = row.original;
			const isShowProposalDates = !!proposalInfo
				&& proposalInfo?.type.name === ERequestItemChanges.changedDates
				&& proposalInfo?.status.name !== ERequestItemChangesAcceptance.refused;

			return (
				<AdaptiveTableCell
					text={formatDate(utcToZonedTime(
						isShowProposalDates ? proposalInfo?.startRentDate : startRentDate,
						timezoneUTC,
					))}
				/>
			);
		},
	},
	{
		header: 'END RENT DATE',
		accessorKey: 'endRentDate',
		cell: ({ getValue, row }: CellContext<IAssetRentRequestCardItem, any>) => {
			const endRentDate = getValue();
			const { proposalInfo } = row.original;
			const isShowProposalDates = !!proposalInfo
				&& proposalInfo?.type.name === ERequestItemChanges.changedDates
				&& proposalInfo?.status.name !== ERequestItemChangesAcceptance.refused;

			return (
				<AdaptiveTableCell
					text={formatDate(utcToZonedTime(
						isShowProposalDates ? proposalInfo?.endRentDate : endRentDate,
						timezoneUTC,
					))}
				/>
			);
		},
	},
	{
		header: 'CHANGES',
		accessorKey: 'changes',
		enableSorting: false,
		cell: ({ row }: CellContext<any, any>) => {
			return (
				<AdaptiveTableCell text={row.original?.proposalInfo?.type.name || ''} />
			);
		},
	},
	{
		header: 'Changes acceptance',
		accessorKey: 'proposalInfo',
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value?.status?.name || ''} />;
		},
	},
	{
		header: 'Approval Status',
		accessorKey: 'approveStatus',
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value?.title || ''} />;
		},
	},
	{
		header: 'PERIOD AVAILABILITY',
		accessorKey: 'periodAvailability',
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell
				className={cn({
					[styles.periodAvailabilityTextUnavailable]: !value,
				})}
				text={value ? 'Available' : 'Unavailable'}
			/>;
		},
	},
	{
		header: 'ACTIONS',
		accessorKey: 'actions',
		enableSorting: false,
		cell: ({ row }: CellContext<any, any>) => {
			return (
				<ActionCellForAssetRentRequestTable
					acceptProposedAsset={acceptProposedAsset}
					asset={row.original}
					declineProposedAsset={declineProposedAsset}
					handleOpenApprovalInfo={handleOpenApprovalInfo}
					handleViewOnMap={handleViewOnMap}
					isAO={isAO}
					isAssetItemsLoading={isAssetItemsLoading}
					openProfileHandler={openProfileHandler}
					openProposeDatesModal={openProposeDatesModal}
					removeProposedAsset={removeProposedAsset}
					request={request}
				/>
			);
		},
	},
]);
